import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { addOrder, finalizeOrderWithInventoryCheck } from '../../store/slices/ordersSlice';
import { addClient } from '../../store/slices/clientsSlice';
import { Order, OrderItem, DeliveryType, CustomerType } from '../../types/order';
import { v4 as uuidv4 } from 'uuid';
import { OrderItemRow } from './components/OrderItemRow';
import { findOrCreateClientByName } from '../../utils/clientMatchingUtils';
import LivePricingPreview from './components/LivePricingPreview';

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableRecipes: Array<{ id: string; name: string }>;
}

interface OrderFormData {
  customerName: string;
  customerType: CustomerType;
  contactName: string;
  email: string;
  email2: string;
  phone: string;
  phone2: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  orderDate: string;
  deliveryType: DeliveryType;
  items: OrderItem[];
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ isOpen, onClose, availableRecipes }) => {
  const dispatch = useAppDispatch();
  const clients = useAppSelector(state => state.clients.clients);

  const getInitialFormData = (): OrderFormData => ({
    customerName: '',
    customerType: 'Individual',
    contactName: '',
    email: '',
    email2: '',
    phone: '',
    phone2: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: '',
    orderDate: new Date().toISOString().split('T')[0],
    deliveryType: 'Delivery',
    items: [
      {
        id: uuidv4(),
        recipeId: '',
        recipeName: '',
        quantity: 1
      }
    ]
  });

  const [formData, setFormData] = useState<OrderFormData>(getInitialFormData());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [showEmail2, setShowEmail2] = useState(false);
  const [showPhone2, setShowPhone2] = useState(false);

  const handleFieldChange = (field: keyof OrderFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field error when user corrects it
    if (attemptedSubmit && validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleItemChange = (index: number, updatedItem: OrderItem) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = updatedItem;
    setFormData(prev => ({ ...prev, items: updatedItems }));

    // Clear item errors when user corrects them
    if (attemptedSubmit) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[`item_${index}_recipe`];
        delete updated[`item_${index}_quantity`];
        return updated;
      });
    }
  };

  const handleAddItem = () => {
    const newItem: OrderItem = {
      id: uuidv4(),
      recipeId: '',
      recipeName: '',
      quantity: 1
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const handleRemoveItem = (index: number) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: updatedItems }));

      // Clear errors for removed item
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[`item_${index}_recipe`];
        delete updated[`item_${index}_quantity`];
        return updated;
      });
    }
  };

  const handleAddEmail2 = () => {
    setShowEmail2(true);
  };

  const handleRemoveEmail2 = () => {
    setShowEmail2(false);
    setFormData(prev => ({ ...prev, email2: '' }));
  };

  const handleAddPhone2 = () => {
    setShowPhone2(true);
  };

  const handleRemovePhone2 = () => {
    setShowPhone2(false);
    setFormData(prev => ({ ...prev, phone2: '' }));
  };

  const validateForm = (isDraft: boolean): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Validate customer name
    if (!formData.customerName.trim()) {
      errors.customerName = 'Customer name is required';
    }

    // Validate at least one item exists
    if (formData.items.length === 0) {
      errors.items = 'At least one order item is required';
    }

    // For finalize, validate all items
    if (!isDraft) {
      formData.items.forEach((item, index) => {
        if (!item.recipeId) {
          errors[`item_${index}_recipe`] = 'Please select a recipe';
        }
        if (item.quantity < 1) {
          errors[`item_${index}_quantity`] = 'Quantity must be at least 1';
        }
      });
    }

    return errors;
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setValidationErrors({});
    setAttemptedSubmit(false);
    setShowEmail2(false);
    setShowPhone2(false);
  };

  const handleSaveDraft = () => {
    const errors = validateForm(true);

    if (Object.keys(errors).length > 0) {
      setAttemptedSubmit(true);
      setValidationErrors(errors);
      return;
    }

    // Find or create client by name
    const { clientId, client, isNewClient } = findOrCreateClientByName(formData.customerName, clients, formData);

    // If new client was created, add it to the store
    if (isNewClient) {
      dispatch(addClient(client));
      const clientName = client.type === 'individual' ? client.name : client.retailerName;
      console.log(`[Draft] New client created: ${clientName} (${client.id})`);
    } else {
      const clientName = client.type === 'individual' ? client.name : client.retailerName;
      console.log(`[Draft] Using existing client: ${clientName} (${client.id})`);
    }

    const newOrder: Order = {
      id: uuidv4(),
      customerName: formData.customerName,
      customerType: formData.customerType,
      contactName: formData.contactName || undefined,
      email: formData.email,
      email2: formData.email2 || undefined,
      phone: formData.phone,
      phone2: formData.phone2 || undefined,
      streetAddress: formData.streetAddress,
      addressLine2: formData.addressLine2,
      city: formData.city,
      province: formData.province,
      postalCode: formData.postalCode,
      orderDate: formData.orderDate,
      deliveryType: formData.deliveryType,
      items: formData.items,
      status: 'Pending',
      isFinalized: false,
      clientId
    };

    dispatch(addOrder(newOrder));
    resetForm();
    onClose();
  };

  const handleFinalize = () => {
    const errors = validateForm(false);

    if (Object.keys(errors).length > 0) {
      setAttemptedSubmit(true);
      setValidationErrors(errors);
      alert('Please correct the errors below');
      return;
    }

    // Find or create client by name
    const { clientId, client, isNewClient } = findOrCreateClientByName(formData.customerName, clients, formData);

    // If new client was created, add it to the store
    if (isNewClient) {
      dispatch(addClient(client));
      const clientName = client.type === 'individual' ? client.name : client.retailerName;
      console.log(`[Finalize] New client created: ${clientName} (${client.id})`);
    } else {
      const clientName = client.type === 'individual' ? client.name : client.retailerName;
      console.log(`[Finalize] Using existing client: ${clientName} (${client.id})`);
    }

    const orderId = uuidv4();
    const newOrder: Order = {
      id: orderId,
      customerName: formData.customerName,
      customerType: formData.customerType,
      contactName: formData.contactName || undefined,
      email: formData.email,
      email2: formData.email2 || undefined,
      phone: formData.phone,
      phone2: formData.phone2 || undefined,
      streetAddress: formData.streetAddress,
      addressLine2: formData.addressLine2,
      city: formData.city,
      province: formData.province,
      postalCode: formData.postalCode,
      orderDate: formData.orderDate,
      deliveryType: formData.deliveryType,
      items: formData.items,
      status: 'Pending',
      isFinalized: false, // Will be set to true by the thunk
      clientId
    };

    // Add the order first (not finalized)
    dispatch(addOrder(newOrder));

    // Then finalize it with inventory check and task generation
    dispatch(finalizeOrderWithInventoryCheck({ orderId }));

    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Create New Order</h2>
          <button style={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        <form style={styles.form} onSubmit={e => e.preventDefault()}>
          {/* Customer Type Selection */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Customer Type</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="customerType"
                  value="Individual"
                  checked={formData.customerType === 'Individual'}
                  onChange={e => handleFieldChange('customerType', e.target.value as CustomerType)}
                  style={styles.radioInput}
                />
                <span style={styles.radioText}>Individual</span>
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="customerType"
                  value="Retailer"
                  checked={formData.customerType === 'Retailer'}
                  onChange={e => handleFieldChange('customerType', e.target.value as CustomerType)}
                  style={styles.radioInput}
                />
                <span style={styles.radioText}>Retailer</span>
              </label>
            </div>
          </div>

          {/* Customer Information Section */}
          <h3 style={styles.sectionTitle}>Customer Information</h3>
          <div style={styles.twoColumnGrid}>
            {/* Left Column */}
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {formData.customerType === 'Retailer' ? 'Retailer Name *' : 'Customer Name *'}
                </label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={e => handleFieldChange('customerName', e.target.value)}
                  style={styles.input}
                  placeholder={formData.customerType === 'Retailer' ? 'Enter retailer name' : 'Enter customer name'}
                  autoFocus
                />
                {attemptedSubmit && validationErrors.customerName && (
                  <div style={styles.error}>{validationErrors.customerName}</div>
                )}
              </div>

              {formData.customerType === 'Retailer' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Contact Name</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={e => handleFieldChange('contactName', e.target.value)}
                    style={styles.input}
                    placeholder="Enter contact person name"
                  />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => handleFieldChange('email', e.target.value)}
                  style={styles.input}
                  placeholder="customer@example.com"
                />
                {!showEmail2 && (
                  <button type="button" onClick={handleAddEmail2} style={styles.addSecondaryButton}>
                    + Add Email
                  </button>
                )}
                {showEmail2 && (
                  <div style={styles.secondaryFieldContainer}>
                    <input
                      type="email"
                      value={formData.email2}
                      onChange={e => handleFieldChange('email2', e.target.value)}
                      style={styles.input}
                      placeholder="secondary@example.com"
                    />
                    <button type="button" onClick={handleRemoveEmail2} style={styles.removeSecondaryButton}>
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => handleFieldChange('phone', e.target.value)}
                  style={styles.input}
                  placeholder="(555) 123-4567"
                />
                {!showPhone2 && (
                  <button type="button" onClick={handleAddPhone2} style={styles.addSecondaryButton}>
                    + Add Phone
                  </button>
                )}
                {showPhone2 && (
                  <div style={styles.secondaryFieldContainer}>
                    <input
                      type="tel"
                      value={formData.phone2}
                      onChange={e => handleFieldChange('phone2', e.target.value)}
                      style={styles.input}
                      placeholder="(555) 987-6543"
                    />
                    <button type="button" onClick={handleRemovePhone2} style={styles.removeSecondaryButton}>
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Street Address</label>
                <input
                  type="text"
                  value={formData.streetAddress}
                  onChange={e => handleFieldChange('streetAddress', e.target.value)}
                  style={styles.input}
                  placeholder="123 Main St"
                />
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Address Line 2</label>
                <input
                  type="text"
                  value={formData.addressLine2}
                  onChange={e => handleFieldChange('addressLine2', e.target.value)}
                  style={styles.input}
                  placeholder="Apt, Suite, Unit, etc."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={e => handleFieldChange('city', e.target.value)}
                  style={styles.input}
                  placeholder="Toronto"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Province</label>
                <input
                  type="text"
                  value={formData.province}
                  onChange={e => handleFieldChange('province', e.target.value)}
                  style={styles.input}
                  placeholder="ON"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Postal Code</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={e => handleFieldChange('postalCode', e.target.value)}
                  style={styles.input}
                  placeholder="A1A 1A1"
                />
              </div>
            </div>
          </div>

          {/* Order Details Section */}
          <h3 style={styles.sectionTitle}>Order Details</h3>
          <div style={styles.formGroup}>
            <label style={styles.label}>Order Date</label>
            <input
              type="date"
              value={formData.orderDate}
              onChange={e => handleFieldChange('orderDate', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Delivery Type</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="Pickup"
                  checked={formData.deliveryType === 'Pickup'}
                  onChange={e => handleFieldChange('deliveryType', e.target.value as DeliveryType)}
                  style={styles.radioInput}
                />
                <span style={styles.radioText}>Pickup</span>
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="Delivery"
                  checked={formData.deliveryType === 'Delivery'}
                  onChange={e => handleFieldChange('deliveryType', e.target.value as DeliveryType)}
                  style={styles.radioInput}
                />
                <span style={styles.radioText}>Delivery</span>
              </label>
            </div>
          </div>

          {/* Order Items Section */}
          <div style={styles.itemsSection}>
            <div style={styles.itemsHeader}>
              <h3 style={styles.sectionTitle}>Order Items *</h3>
              <button type="button" onClick={handleAddItem} style={styles.addItemButton}>
                + Add Item
              </button>
            </div>

            {attemptedSubmit && validationErrors.items && <div style={styles.error}>{validationErrors.items}</div>}

            {formData.items.map((item, index) => (
              <div key={item.id}>
                <OrderItemRow
                  item={item}
                  recipes={availableRecipes}
                  onChange={updatedItem => handleItemChange(index, updatedItem)}
                  onRemove={() => handleRemoveItem(index)}
                />
                {attemptedSubmit && validationErrors[`item_${index}_recipe`] && (
                  <div style={styles.error}>{validationErrors[`item_${index}_recipe`]}</div>
                )}
                {attemptedSubmit && validationErrors[`item_${index}_quantity`] && (
                  <div style={styles.error}>{validationErrors[`item_${index}_quantity`]}</div>
                )}
              </div>
            ))}

            {formData.items.length === 0 && <div style={styles.emptyState}>No items added yet</div>}
          </div>

          {/* Live Pricing Preview */}
          <LivePricingPreview
            items={formData.items}
            customerType={formData.customerType}
            deliveryType={formData.deliveryType}
            city={formData.city}
          />

          {/* Footer Buttons */}
          <div style={styles.modalFooter}>
            <button type="button" onClick={handleClose} style={styles.cancelButton}>
              Cancel
            </button>
            <div style={styles.rightButtons}>
              <button type="button" onClick={handleSaveDraft} style={styles.draftButton}>
                Save as Draft
              </button>
              <button type="button" onClick={handleFinalize} style={styles.finalizeButton}>
                Finalize Order
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  form: {
    padding: '20px'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginTop: '16px',
    marginBottom: '12px'
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555'
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  error: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px'
  },
  itemsSection: {
    marginTop: '24px',
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px'
  },
  itemsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  addItemButton: {
    padding: '6px 12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  emptyState: {
    textAlign: 'center',
    color: '#999',
    padding: '20px',
    fontSize: '14px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  rightButtons: {
    display: 'flex',
    gap: '12px'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  draftButton: {
    padding: '10px 20px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  finalizeButton: {
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  radioGroup: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px'
  },
  radioInput: {
    marginRight: '8px',
    cursor: 'pointer'
  },
  radioText: {
    color: '#333'
  },
  addSecondaryButton: {
    marginTop: '8px',
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: '#2196F3',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'left',
    display: 'inline-block'
  },
  secondaryFieldContainer: {
    marginTop: '8px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  removeSecondaryButton: {
    padding: '8px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
    lineHeight: '1',
    minWidth: '36px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default CreateOrderModal;
